import { ORPCError } from "@orpc/client";
import {
  isReportReasonAllowed,
  type ModerationSubject,
  ReportReason,
} from "@pax/api-contract/moderation/v1alpha1";
import { REPORT_COPY, REPORT_REASONS, SUBJECT_NOUNS } from "@pax/moderation-copy";
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  focusHaloFlush,
  panelMotion,
  Radio,
  RadioGroup,
  Textarea,
} from "@pax/ui";
import { useMutation } from "@tanstack/react-query";
import { useId, useState } from "react";
import { useAccountAction } from "@/features/auth/account-action";
import { api, reachable, requestErrorMessage } from "@/lib/api";

/** A reason to report, or a copyright claim, which goes to the DMCA contact instead. */
type Choice = ReportReason | "ownership";

const MAX_DETAILS = 1000;

/**
 * Reporting one item: `start` opens the dialog once the reader has a full account, and `dialog` is
 * the dialog to render beside the control that starts it.
 */
export function useReport(subject: ModerationSubject) {
  const requireAccount = useAccountAction("report");
  const [open, setOpen] = useState(false);
  const [attempt, setAttempt] = useState(0);
  return {
    start: () =>
      requireAccount(() => {
        setAttempt(count => count + 1);
        setOpen(true);
      }),
    dialog: <ReportDialog key={attempt} subject={subject} open={open} onOpenChange={setOpen} />,
  };
}

function ReportDialog({
  subject,
  open,
  onOpenChange,
}: {
  subject: ModerationSubject;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const formId = useId();
  const detailsId = useId();
  const [choice, setChoice] = useState<Choice | null>(null);
  const [details, setDetails] = useState("");
  const typed = details.trim();
  const report = useMutation({
    mutationFn: (reason: ReportReason) =>
      reachable(
        api.moderation.reports.create({
          body: { subject, reason, ...(typed && { details: typed }) },
        }),
      ),
  });
  const duplicate = report.error instanceof ORPCError && report.error.code === "CONFLICT";
  const choices: Choice[] = [
    ...ReportReason.options.filter(
      reason => reason !== "other" && isReportReasonAllowed(subject.type, reason),
    ),
    ...(isReportReasonAllowed(subject.type, "other") ? (["ownership", "other"] as const) : []),
  ];
  const ready = choice !== null && choice !== "ownership" && (choice !== "other" || typed !== "");

  if (report.isSuccess || duplicate) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {duplicate
                ? REPORT_COPY.duplicate(SUBJECT_NOUNS[subject.type])
                : REPORT_COPY.submitted}
            </DialogTitle>
            <DialogDescription>{REPORT_COPY.noOutcomePromise}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => onOpenChange(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!report.isPending) onOpenChange(next);
      }}
    >
      <DialogContent className="max-h-[calc(100dvh-2rem)] max-w-md grid-rows-[auto_minmax(0,1fr)_auto]">
        <DialogHeader>
          <DialogTitle>{REPORT_COPY.reasonPrompt}</DialogTitle>
        </DialogHeader>
        <form
          id={formId}
          className="-mx-5 min-h-0 overflow-y-auto px-5"
          onSubmit={event => {
            event.preventDefault();
            if (ready) report.mutate(choice);
          }}
        >
          <RadioGroup
            aria-label={REPORT_COPY.reasonPrompt}
            value={choice}
            onValueChange={value => setChoice(value as Choice)}
            disabled={report.isPending}
            className="-mx-2 flex flex-col"
          >
            {choices.map(option => {
              const words =
                option === "ownership"
                  ? {
                      title: REPORT_COPY.ownershipTitle,
                      description: REPORT_COPY.ownershipDescription,
                    }
                  : REPORT_REASONS[option];
              return (
                <Radio
                  key={option}
                  value={option}
                  className={cn(
                    "group flex w-full items-start gap-3 rounded-xl px-2 py-2.5 text-left hover:bg-foreground/5 data-checked:bg-foreground/[0.07]",
                    panelMotion,
                    focusHaloFlush,
                  )}
                >
                  <span
                    aria-hidden
                    className="mt-0.5 grid size-4 shrink-0 place-items-center rounded-full ring-1 ring-foreground/30 ring-inset group-data-checked:bg-foreground group-data-checked:ring-foreground"
                  >
                    <span className="size-1.5 rounded-full bg-background opacity-0 group-data-checked:opacity-100" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium">{words.title}</span>
                    <span className="mt-0.5 block text-xs leading-5 text-muted-foreground">
                      {words.description}
                    </span>
                  </span>
                </Radio>
              );
            })}
          </RadioGroup>

          {choice === "ownership" ? (
            <p className="mt-3 rounded-xl bg-foreground/5 px-3 py-2.5 text-sm leading-6">
              {REPORT_COPY.ownership}
            </p>
          ) : (
            choice !== null && (
              <div className="mt-3">
                <label htmlFor={detailsId} className="text-xs leading-5 text-muted-foreground">
                  {REPORT_COPY.explanationPrompt}
                  {choice !== "other" && " (Optional)"}
                </label>
                <Textarea
                  id={detailsId}
                  value={details}
                  onChange={event => setDetails(event.target.value)}
                  maxLength={MAX_DETAILS}
                  required={choice === "other"}
                  disabled={report.isPending}
                  className="mt-1.5 max-h-40"
                />
              </div>
            )
          )}

          {report.error && !duplicate && (
            <p role="alert" className="mt-3 text-sm text-destructive">
              {requestErrorMessage(report.error)}
            </p>
          )}
        </form>
        <DialogFooter>
          {choice === "ownership" ? (
            <Button onClick={() => onOpenChange(false)}>Done</Button>
          ) : (
            <>
              <Button
                variant="outline"
                disabled={report.isPending}
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" form={formId} disabled={!ready || report.isPending}>
                {report.isPending ? "Reporting…" : REPORT_COPY.cta}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
